/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState } from 'react';
import { AiSignal } from '../types/active-signals';
import { TokenAnalytics } from '../types/analytics';
import { AiEngine } from '../lib/ai/engine';
import { TokenInsights } from './TokenInsights';
import { X, ExternalLink, Loader2, Brain, XCircle } from 'lucide-react';

interface AnalysisPopupCardProps {
    signal: AiSignal;
    onClose: () => void;
    onExecute: () => void;
}

export const AnalysisPopupCard: React.FC<AnalysisPopupCardProps> = ({ signal, onClose, onExecute }) => {
    const [analytics, setAnalytics] = useState<TokenAnalytics | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const analyze = async () => {
            // Artificial delay to show the "Analyzing..." state clearly
            await new Promise(r => setTimeout(r, 1500));
            try {
                const data = await AiEngine.analyzeToken(signal.token, signal.priceAtSignal);
                if (mounted) {
                    setAnalytics(data);
                    setLoading(false);
                }
            } catch (e) {
                console.error(e);
                if (mounted) setLoading(false);
            }
        };
        analyze();
        return () => { mounted = false; };
    }, [signal]);

    return (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div 
                className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-fade-in-up border border-stone-200"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-stone-100 flex justify-between items-center bg-stone-50/50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-stone-900 rounded-full flex items-center justify-center text-white font-serif font-bold shadow-md">
                            {signal.token.symbol[0]}
                        </div>
                        <div>
                            <div className="font-bold text-stone-900 leading-tight text-lg">{signal.token.name}</div>
                            <div className="text-xs text-stone-500 font-mono flex items-center gap-1">
                                <span>Signal Confidence:</span>
                                <span className={`font-bold ${signal.confidence > 80 ? 'text-green-600' : 'text-amber-600'}`}>
                                    {signal.confidence}%
                                </span>
                            </div>
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2 hover:bg-stone-200 rounded-md transition-colors text-stone-400 hover:text-stone-900"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto custom-scrollbar relative min-h-[300px] flex-1 bg-[#F9F8F4]/30">
                    {loading ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10 space-y-4">
                            <div className="relative">
                                <div className="w-16 h-16 border-4 border-stone-100 border-t-nobel-gold rounded-full animate-spin"></div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Brain size={24} className="text-stone-300 animate-pulse" />
                                </div>
                            </div>
                            <div className="text-center">
                                <h3 className="font-serif text-xl text-stone-900 mb-1">Analyzing...</h3>
                                <p className="text-xs text-stone-500 font-mono">Running Neural Inference on {signal.token.properties?.chain || 'Chain'}</p>
                            </div>
                            <div className="w-64 space-y-1">
                                <div className="h-1 w-full bg-stone-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-nobel-gold animate-[loading_1.5s_ease-in-out_infinite] w-1/3"></div>
                                </div>
                                <div className="flex justify-between text-[10px] text-stone-400">
                                    <span>Volume</span>
                                    <span>Sentiment</span>
                                    <span>Whales</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="animate-fade-in space-y-6">
                            <TokenInsights token={signal.token} currentPrice={signal.priceAtSignal} />
                            
                            <div className="bg-white p-4 rounded-xl border border-stone-100 shadow-sm">
                                <div className="text-xs font-bold text-stone-400 uppercase mb-2">Original Signal</div>
                                <p className="text-sm text-stone-600 italic leading-relaxed">"{signal.reasoning}"</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-stone-100 bg-white flex gap-3">
                    <button 
                        onClick={onClose}
                        className="flex-1 py-3 border border-stone-200 text-stone-500 font-bold rounded-md hover:bg-stone-50 hover:text-stone-900 transition-colors flex items-center justify-center gap-2"
                    >
                        Dismiss <XCircle size={16} />
                    </button>
                    <button 
                        onClick={() => { onClose(); onExecute(); }}
                        disabled={loading}
                        className="flex-[2] py-3 bg-stone-900 text-white font-bold rounded-md hover:bg-nobel-gold hover:text-stone-900 transition-colors flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed group"
                    >
                        Execute {signal.type} Order 
                        <ExternalLink size={16} className="group-hover:translate-x-0.5 transition-transform" />
                    </button>
                </div>
            </div>
        </div>
    );
};